import { TokenListItem } from '~/components/component-library/TokenSelector/TokenSelectorTokenList'
import {
  NetworkFilterState,
  SortBy,
} from '~/components/component-library/TokenSelector/types'
import { POPULAR_TOKENS } from '~/constants/addresses'
import { SupportedChainId } from '~/constants/chains'
import { AggregatedBalance } from '~/hooks/useAggregatedBalances'
import { FrequentlyUsedToken } from '~/hooks/useFrequentlyUsedTokens'
import { RelayCurrencySearchResultWithChainId } from '~/hooks/useRelayCurrencySearch'
import { BackendTokenDict, Multichain } from '~/types'
import { Address } from '~/utils/address'

type TokenRef = { address: string; chainId: number }

type DisplayToken =
  | { type: 'balance'; data: AggregatedBalance }
  | {
      type: 'relay'
      data: RelayCurrencySearchResultWithChainId
      ownedBalance?: AggregatedBalance
    }
  | { type: 'popular'; data: TokenRef }
  | { type: 'whitelist'; data: TokenRef }
  | { type: 'metadata'; data: TokenRef }

export type GetDisplayTokensParams = {
  balances: AggregatedBalance[]
  relayResults: RelayCurrencySearchResultWithChainId[]
  activeChainIds: number[]
  networkFilter: NetworkFilterState
  searchQuery: string
  tokenFilter?: (token: { address: string; chainId: number }) => boolean
  /** Treat tokenFilter as a strict allowlist: apply it to every token,
   *  including relay-network tokens and tokens on non-active chains, which
   *  are normally exempt. Defaults to false. */
  strictTokenFilter?: boolean
  showBalances: boolean
  frequentlyUsedTokens: FrequentlyUsedToken[]
  /** Org's enabled-token list; consulted as an address-paste fallback when
   *  Relay's index doesn't cover the searched token. */
  tokenWhitelist?: Multichain<BackendTokenDict>
  /** Exact address metadata matches; consulted as a final address-paste fallback. */
  exactAddressMetadataTokens?: TokenRef[]
  /** Tokens to always show first, regardless of balance or frequency */
  promotedTokens?: { address: string; chainId: number }[]
  /** Include freq-used tokens on non-active chains in the Top tab. Defaults to false. */
  allowFrequentlyUsedFromNonActiveChains?: boolean
  /**
   * Picks which tier ordering to use:
   * - 'value' activates the balance tier (balances first by USD desc).
   * - 'frequency' skips the balance tier — same ordering used when
   *   showBalances is false.
   */
  sortBy: SortBy
}

const normalizeKey = (chainId: number, address: string) =>
  `${chainId}-${Address.format(address)}`

export function getDisplayTokens({
  balances,
  relayResults,
  activeChainIds,
  networkFilter,
  searchQuery,
  tokenFilter,
  strictTokenFilter = false,
  showBalances,
  frequentlyUsedTokens,
  tokenWhitelist,
  exactAddressMetadataTokens,
  promotedTokens,
  allowFrequentlyUsedFromNonActiveChains = false,
  sortBy,
}: GetDisplayTokensParams): TokenListItem[] {
  const selectedChainFilter =
    networkFilter.type === 'all' ? null : networkFilter.chainId

  // ---- Lookup maps --------------------------------------------------------

  const balancesMap = new Map(
    balances.map((b) => [normalizeKey(b.chainId, b.address), b]),
  )

  const popularTokenSet = new Set(
    activeChainIds.flatMap((chainId) =>
      (POPULAR_TOKENS[chainId as SupportedChainId] ?? []).map((addr) =>
        normalizeKey(chainId, addr),
      ),
    ),
  )

  const popularTokenOrder = new Map(
    activeChainIds
      .flatMap((chainId) =>
        (POPULAR_TOKENS[chainId as SupportedChainId] ?? []).map((address) =>
          normalizeKey(chainId, address),
        ),
      )
      .map((key, index) => [key, index] as const),
  )

  const frequencyMap = new Map(
    frequentlyUsedTokens.map((t) => [
      normalizeKey(t.chainId, t.address),
      t.uses,
    ]),
  )

  const promotedTokenSet = new Set(
    (promotedTokens ?? []).map((t) => normalizeKey(t.chainId, t.address)),
  )

  const promotedTokenOrder = new Map(
    (promotedTokens ?? []).map(
      (t, i) => [normalizeKey(t.chainId, t.address), i] as const,
    ),
  )

  // ---- Candidate list (DisplayToken[]) ------------------------------------

  const displayTokens = buildCandidateList({
    balances,
    relayResults,
    activeChainIds,
    networkFilter,
    searchQuery,
    tokenFilter,
    strictTokenFilter,
    selectedChainFilter,
    balancesMap,
    popularTokenSet,
    frequentlyUsedTokens,
    tokenWhitelist,
    exactAddressMetadataTokens,
    allowFrequentlyUsedFromNonActiveChains,
  })

  // ---- Convert → deduplicate → sort ---------------------------------------

  const mapped = displayTokens.map((token): TokenListItem => {
    const balance =
      token.type === 'balance'
        ? token.data
        : token.type === 'relay'
          ? token.ownedBalance
          : undefined
    const isRelayNetwork = !activeChainIds.includes(token.data.chainId)

    return {
      address: token.data.address,
      chainId: token.data.chainId,
      balance: showBalances ? (balance ?? undefined) : undefined,
      isRelayNetwork,
    }
  })

  const deduped = deduplicate(mapped)

  return sortTokens(deduped, {
    sortBy,
    balancesMap,
    frequencyMap,
    popularTokenSet,
    popularTokenOrder,
    promotedTokenSet,
    promotedTokenOrder,
  })
}

// ---------------------------------------------------------------------------
// Step 1 — Build candidate list
//
// Determines which tokens to show based on the current mode:
//   • Relay network  → featured tokens, or relay search results if searching
//   • Search         → relay search results enriched with owned balances
//   • Default browse → user balances + frequently used tokens (not already in
//                      balances or popular) + popular tokens (not already in
//                      balances), all filtered by the active chain/token filters
// ---------------------------------------------------------------------------

function buildCandidateList({
  balances,
  relayResults,
  activeChainIds,
  networkFilter,
  searchQuery,
  tokenFilter,
  strictTokenFilter,
  selectedChainFilter,
  balancesMap,
  popularTokenSet,
  frequentlyUsedTokens,
  tokenWhitelist,
  exactAddressMetadataTokens,
  allowFrequentlyUsedFromNonActiveChains,
}: {
  balances: AggregatedBalance[]
  relayResults: RelayCurrencySearchResultWithChainId[]
  activeChainIds: number[]
  networkFilter: NetworkFilterState
  searchQuery: string
  tokenFilter?: (token: { address: string; chainId: number }) => boolean
  strictTokenFilter: boolean
  selectedChainFilter: number | null
  balancesMap: Map<string, AggregatedBalance>
  popularTokenSet: Set<string>
  frequentlyUsedTokens: FrequentlyUsedToken[]
  tokenWhitelist?: Multichain<BackendTokenDict>
  exactAddressMetadataTokens?: TokenRef[]
  allowFrequentlyUsedFromNonActiveChains: boolean
}): DisplayToken[] {
  const filterByChain = <T extends { chainId: number }>(items: T[]): T[] =>
    selectedChainFilter === null
      ? items
      : items.filter((item) => item.chainId === selectedChainFilter)

  const applyTokenFilter = (items: DisplayToken[]): DisplayToken[] => {
    if (!tokenFilter) return items
    return items.filter((item) => {
      const { address, chainId } = item.data
      // Skip tokenFilter for non-active chains so frequently-used / external
      // tokens pass through — unless the filter is a strict allowlist.
      if (!strictTokenFilter && !activeChainIds.includes(chainId)) return true
      return tokenFilter({ address, chainId })
    })
  }

  // Relay network mode — show featured tokens or search results. These are
  // non-active chains, so applyTokenFilter is a pass-through unless the
  // filter is strict.
  if (networkFilter.type === 'relay') {
    if (searchQuery.length > 0) {
      return applyTokenFilter(
        relayResults.map((token) => ({
          type: 'relay' as const,
          data: token,
        })),
      )
    }

    return applyTokenFilter(
      networkFilter.metadata.featuredTokens.map((token) => ({
        type: 'relay' as const,
        data: { ...token, chainId: networkFilter.chainId },
      })),
    )
  }

  // Search mode — relay results + address-paste fallbacks.
  //
  // Relay's `currencies/v2` index doesn't cover every ERC-20 (e.g. long-tail
  // tokens never traded through Relay), so when the search term is a valid
  // address we additionally surface entries from the user's balances and the
  // org's enabled-token list. Without this, a token the user already holds
  // or has explicitly enabled in Token Settings becomes unfindable here just
  // because Relay hasn't indexed it.
  if (searchQuery.length > 0) {
    const relayItems: DisplayToken[] = filterByChain(relayResults).map(
      (token) => ({
        type: 'relay' as const,
        data: token,
        ownedBalance: balancesMap.get(
          normalizeKey(token.chainId, token.address),
        ),
      }),
    )

    const isAddressSearch = Address.validate(searchQuery)

    const balanceMatches: DisplayToken[] = isAddressSearch
      ? filterByChain(balances)
          .filter((b) => Address.equal(b.address, searchQuery))
          .map((b) => ({ type: 'balance' as const, data: b }))
      : []

    const whitelistMatches: DisplayToken[] = isAddressSearch
      ? Object.entries(tokenWhitelist ?? {}).flatMap(
          ([chainIdStr, tokenDict]): DisplayToken[] => {
            const chainId = Number(chainIdStr)
            if (selectedChainFilter !== null && chainId !== selectedChainFilter)
              return []
            return Object.values(tokenDict)
              .filter((t) => Address.equal(t.address, searchQuery))
              .map((t) => ({
                type: 'whitelist' as const,
                data: { address: t.address, chainId },
              }))
          },
        )
      : []

    const metadataMatches: DisplayToken[] = isAddressSearch
      ? filterByChain(exactAddressMetadataTokens ?? [])
          .filter((t) => Address.equal(t.address, searchQuery))
          .map((t) => ({ type: 'metadata' as const, data: t }))
      : []

    return applyTokenFilter([
      ...relayItems,
      ...balanceMatches,
      ...whitelistMatches,
      ...metadataMatches,
    ])
  }

  // Default mode — balances + frequently used + popular
  const balanceItems: DisplayToken[] = filterByChain(balances).map(
    (balance) => ({ type: 'balance' as const, data: balance }),
  )

  const frequentlyUsedItems: DisplayToken[] = filterByChain(
    frequentlyUsedTokens
      .filter((t) => {
        if (
          !allowFrequentlyUsedFromNonActiveChains &&
          !activeChainIds.includes(t.chainId)
        )
          return false
        const key = normalizeKey(t.chainId, t.address)
        return !balancesMap.has(key) && !popularTokenSet.has(key)
      })
      .map((t) => ({ address: t.address, chainId: t.chainId })),
  ).map((token) => ({
    type: 'popular' as const,
    data: { address: token.address, chainId: token.chainId },
  }))

  const popularItems: DisplayToken[] = filterByChain(
    activeChainIds.flatMap((chainId) =>
      (POPULAR_TOKENS[chainId as SupportedChainId] ?? [])
        .filter((address) => !balancesMap.has(normalizeKey(chainId, address)))
        .map((address) => ({ address, chainId })),
    ),
  ).map((token) => ({
    type: 'popular' as const,
    data: { address: token.address, chainId: token.chainId },
  }))

  return applyTokenFilter([
    ...balanceItems,
    ...frequentlyUsedItems,
    ...popularItems,
  ])
}

// ---------------------------------------------------------------------------
// Step 2 — Deduplicate
// ---------------------------------------------------------------------------

function deduplicate(items: TokenListItem[]): TokenListItem[] {
  const seen = new Map<string, TokenListItem>()

  items.forEach((item) => {
    const key = normalizeKey(item.chainId, item.address)
    const existing = seen.get(key)

    if (!existing) {
      seen.set(key, item)
      return
    }

    // Prefer the entry that includes balance info
    if (!existing.balance && item.balance) {
      seen.set(key, item)
    }
  })

  return Array.from(seen.values())
}

// ---------------------------------------------------------------------------
// Step 3 — Tiered sort
//
//   Tier -1  promoted tokens          → sorted by promotedTokens array order
//
//   When sortBy is 'value' (only allowed alongside showBalances=true):
//     Tier 0  tokens with balances     → sorted by USD value desc
//     Tier 1  frequently used tokens   → sorted by use-count desc
//     Tier 2  popular tokens           → sorted by POPULAR_TOKENS order
//     Tier 3  everything else          → sorted by address (stable)
//
//   When sortBy is 'frequency':
//     (skip tier 0 — balances get no special treatment)
//     Tier 1 → Tier 2 → Tier 3
//
//   Within each tier, relay-only tokens sort last as a tiebreaker — only
//   when the tier's primary comparator returns 0.
// ---------------------------------------------------------------------------

type SortContext = {
  sortBy: SortBy
  balancesMap: Map<string, AggregatedBalance>
  frequencyMap: Map<string, number>
  popularTokenSet: Set<string>
  popularTokenOrder: Map<string, number>
  promotedTokenSet: Set<string>
  promotedTokenOrder: Map<string, number>
}

function sortTokens(
  items: TokenListItem[],
  sortContext: SortContext,
): TokenListItem[] {
  const getTier = (item: TokenListItem): number => {
    const key = normalizeKey(item.chainId, item.address)
    if (sortContext.promotedTokenSet.has(key)) return -1
    if (sortContext.sortBy === 'value' && sortContext.balancesMap.has(key))
      return 0
    if ((sortContext.frequencyMap.get(key) ?? 0) > 0) return 1
    if (sortContext.popularTokenSet.has(key)) return 2
    return 3
  }

  return [...items].sort((a, b) => {
    const tierA = getTier(a)
    const tierB = getTier(b)

    if (tierA !== tierB) return tierA - tierB

    const keyA = normalizeKey(a.chainId, a.address)
    const keyB = normalizeKey(b.chainId, b.address)

    // Tier-specific primary comparator. Returns 0 when items tie within
    // the tier — the relay-last tiebreaker below resolves those.
    let tierOrder = 0
    if (tierA === -1) {
      // Tier -1 — promoted tokens, by array order
      const orderA = sortContext.promotedTokenOrder.get(keyA) ?? Infinity
      const orderB = sortContext.promotedTokenOrder.get(keyB) ?? Infinity
      tierOrder = orderA - orderB
    } else if (tierA === 0) {
      // Tier 0 — USD value desc
      const valA = sortContext.balancesMap.get(keyA)?.totalUsdValue ?? 0
      const valB = sortContext.balancesMap.get(keyB)?.totalUsdValue ?? 0
      tierOrder = valB - valA
    } else if (tierA === 1) {
      // Tier 1 — use-count desc
      const usesA = sortContext.frequencyMap.get(keyA) ?? 0
      const usesB = sortContext.frequencyMap.get(keyB) ?? 0
      tierOrder = usesB - usesA
    } else if (tierA === 2) {
      // Tier 2 — POPULAR_TOKENS order
      const orderA = sortContext.popularTokenOrder.get(keyA) ?? Infinity
      const orderB = sortContext.popularTokenOrder.get(keyB) ?? Infinity
      tierOrder = orderA - orderB
    } else {
      // Tier 3 — alphabetical by address
      tierOrder = a.address.localeCompare(b.address)
    }

    if (tierOrder !== 0) return tierOrder

    // Tiebreaker: relay-only tokens sort last
    if (a.isRelayNetwork !== b.isRelayNetwork) return a.isRelayNetwork ? 1 : -1
    return 0
  })
}
